/**
 * /admin/banks — the Banks list (Banks arc step 1, migration 0112; Pedro 2026-10-20).
 *
 * One record per bank: Mnemonic, Name, Address, SWIFT/BIC and up to 10 Contacts (Name,
 * Email, Phone, Mobile, Notes, and Trade orders To / Cc tick columns — TRADE-FLOW T-F4 / TF-S1,
 * migration 0122 — in line). Accounts pick their bank by mnemonic; renaming a
 * bank here updates every account of it. Only Admin opens this page (Pedro F3).
 * Edit → Save / Discard (§3g Pattern A, leave-page guard); Delete is its own confirmed
 * action, Edit-gated, and refused while the bank has accounts (C-172). The review notes
 * (what migration 0112 decided while building the list from the accounts) are listed
 * per bank, each dismissed on its own.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Container, Grid, Group, Paper, Stack, Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { IconArrowBackUp, IconBuilding, IconCheck, IconPencil, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  MAX_BANK_CONTACTS, REVIEW_NOTE_KINDS, banksApi, type BankContact, type BankDetail, type BankInput,
} from '../api/banks';
import { ApiError } from '../api/client';
import { AccessDeniedAlert } from '../components/RequireAccess';
import { useAlertModal } from '../components/AlertModalProvider';
import { useCurrentUser } from '../hooks/useAuth';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const EMPTY_CONTACT: BankContact = { name: '', email: '', phone: '', mobile: '', notes: '', trade_role: null };
const errMsg = (e: unknown) => (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Something went wrong.');

function toDraft(b: BankDetail | null): BankInput {
  return b
    ? { mnemonic: b.mnemonic, name: b.name, address: b.address ?? '', swift_bic: b.swift_bic ?? '',
        contacts: b.contacts.map(({ name, email, phone, mobile, notes, trade_role }) =>
          ({ name, email, phone, mobile, notes, trade_role: trade_role ?? null })) }
    : { mnemonic: '', name: '', address: '', swift_bic: '', contacts: [] };
}
const same = (a: BankInput, b: BankInput) => JSON.stringify(a) === JSON.stringify(b);

export function BanksPage() {
  useDocumentTitle('Banks');
  const { user } = useCurrentUser();
  const qc = useQueryClient();
  const { confirm } = useAlertModal();
  const listQ = useQuery({ queryKey: ['banks'], queryFn: () => banksApi.list() });
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const detailQ = useQuery({
    queryKey: ['bank', selected], queryFn: () => banksApi.get(selected as string),
    enabled: !!selected && selected !== 'new',
  });
  const detail = selected === 'new' ? null : detailQ.data ?? null;

  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<BankInput>(toDraft(null));
  const base = useMemo(() => toDraft(detail), [detail]);
  useEffect(() => { if (!editMode) setDraft(base); }, [base, editMode]);
  const dirty = editMode && !same(draft, base);

  const refresh = (id?: string) => {
    qc.invalidateQueries({ queryKey: ['banks'] });
    if (id) qc.invalidateQueries({ queryKey: ['bank', id] });
    qc.invalidateQueries({ queryKey: ['accounts'] });   // names / mnemonics follow a rename
  };
  const save = useMutation({
    mutationFn: (d: BankInput) => (selected === 'new' ? banksApi.create(d) : banksApi.update(selected as string, d)),
    onSuccess: (b) => {
      refresh(b.id);
      setEditMode(false);
      setSelected(b.id);
      notifications.show({ color: 'green', message: `Saved ${b.mnemonic} · ${b.name}` });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => banksApi.remove(id),
    onSuccess: (b) => { refresh(); setSelected(null); setEditMode(false);
      notifications.show({ color: 'green', message: `Deleted ${b.mnemonic} · ${b.name}` }); },
  });
  const dismiss = useMutation({
    mutationFn: (noteId: string) => banksApi.dismissNote(selected as string, noteId),
    onSuccess: () => refresh(selected as string),
  });

  const onSave = async (): Promise<boolean> => {
    try { await save.mutateAsync(draft); return true; } catch { return false; }
  };
  const confirmDiscard = useDirtyFormGuard(dirty, onSave);

  if (!user) return null;
  if (user.role !== 'admin') return <AccessDeniedAlert />;

  const pick = (id: string | 'new') => confirmDiscard(() => {
    setSelected(id); setEditMode(id === 'new'); save.reset();
    if (id === 'new') setDraft(toDraft(null));
  });
  const setContact = (i: number, k: keyof BankContact, v: string | null) =>
    setDraft((d) => ({ ...d, contacts: d.contacts.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const banks = listQ.data ?? [];
  const accountsOfBank = detail?.accounts ?? 0;

  return (
    <Container size="xl">
      <Stack>
        <Group justify="space-between">
          <Group gap="xs"><IconBuilding size={22} /><Title order={2}>Banks</Title></Group>
          <Button leftSection={<IconPlus size={16} />} onClick={() => pick('new')} disabled={editMode}>New bank</Button>
        </Group>
        <Text size="sm" c="dimmed">
          Each account picks its bank by mnemonic. The bank's name, address, SWIFT/BIC and contacts are kept here once
          and shown on every account of the bank; renaming a bank updates all its accounts.
        </Text>
        {listQ.error && <Alert color="red">{errMsg(listQ.error)}</Alert>}

        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Table highlightOnHover withTableBorder fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Mnemonic</Table.Th><Table.Th>Name</Table.Th><Table.Th>SWIFT/BIC</Table.Th>
                  <Table.Th ta="right">Accounts</Table.Th><Table.Th ta="right">Contacts</Table.Th><Table.Th>To review</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {banks.length === 0 && !listQ.isLoading && (
                  <Table.Tr><Table.Td colSpan={6}><Text c="dimmed" size="sm">No banks yet. Add one with New bank.</Text></Table.Td></Table.Tr>
                )}
                {banks.map((b) => (
                  <Table.Tr key={b.id} onClick={() => pick(b.id)} style={{ cursor: 'pointer' }}
                    bg={selected === b.id ? 'var(--mantine-color-azul-0)' : undefined}>
                    <Table.Td><Text ff="monospace" fw={600} size="sm">{b.mnemonic}</Text></Table.Td>
                    <Table.Td>{b.name}</Table.Td>
                    <Table.Td><Text ff="monospace" size="sm">{b.swift_bic ?? '-'}</Text></Table.Td>
                    <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                      {b.accounts > 0 ? (
                        <Tooltip withArrow label="Open these accounts in the Accounts list (every status)">
                          <Anchor component={Link} to={`/accounts?mnemonic=${encodeURIComponent(b.mnemonic)}`} size="sm">
                            {b.accounts}
                          </Anchor>
                        </Tooltip>
                      ) : 0}
                    </Table.Td>
                    <Table.Td ta="right">{b.contacts_count}</Table.Td>
                    <Table.Td>
                      {b.notes_count > 0 && (
                        <Tooltip withArrow label="Notes left when the list was built from the accounts: open the bank to review them">
                          <Badge color="yellow" variant="light">{b.notes_count}</Badge>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 7 }}>
            {!selected ? (
              <Text c="dimmed" size="sm">Choose a bank to see or edit it.</Text>
            ) : (
              <Paper withBorder p="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Title order={4}>{selected === 'new' ? 'New bank' : `${detail?.mnemonic ?? ''} · ${detail?.name ?? ''}`}</Title>
                    {!editMode ? (
                      <Button leftSection={<IconPencil size={16} />} onClick={() => setEditMode(true)} disabled={!detail}>Edit</Button>
                    ) : (
                      <Group gap="xs">
                        {selected !== 'new' && (
                          <Tooltip withArrow disabled={accountsOfBank === 0}
                            label={`${accountsOfBank} account${accountsOfBank === 1 ? '' : 's'} use this bank: move them to another bank first`}>
                            <span>
                              <Button color="red" variant="light" leftSection={<IconTrash size={16} />}
                                disabled={accountsOfBank > 0} loading={remove.isPending}
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: `Delete ${detail?.mnemonic} · ${detail?.name}?`,
                                    message: 'The bank leaves the list (it stays in the audit log). No account uses it.',
                                    confirmLabel: 'Yes, delete', cancelLabel: 'No', destructive: true,
                                  });
                                  if (ok) remove.mutate(selected);
                                }}>
                                Delete bank
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Button variant="default" leftSection={<IconArrowBackUp size={16} />}
                          onClick={() => confirmDiscard(() => { setEditMode(false); setDraft(base);
                            if (selected === 'new') setSelected(null); save.reset(); })}>
                          Discard
                        </Button>
                        <Button color="azul" loading={save.isPending} disabled={!dirty} onClick={() => void onSave()}>Save changes</Button>
                      </Group>
                    )}
                  </Group>
                  {save.error && <Alert color="red" title="Not saved">{errMsg(save.error)}</Alert>}
                  {remove.error && <Alert color="red" title="Not deleted">{errMsg(remove.error)}</Alert>}

                  <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <Stack gap="sm">
                      <Grid>
                        <Grid.Col span={3}>
                          {/* No description under the label: it pushed this box below its
                              neighbours (Pedro's smoke). The rule is in the placeholder and
                              the server's message. */}
                          <TextInput label="Mnemonic" required maxLength={8} value={draft.mnemonic}
                            placeholder="2–8 capitals or digits"
                            onChange={(e) => setDraft({ ...draft, mnemonic: e.currentTarget.value.toUpperCase() })} />
                        </Grid.Col>
                        <Grid.Col span={6}>
                          <TextInput label="Name" required value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })} />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <TextInput label="SWIFT / BIC" maxLength={11} value={draft.swift_bic ?? ''}
                            onChange={(e) => setDraft({ ...draft, swift_bic: e.currentTarget.value.toUpperCase() })} />
                        </Grid.Col>
                      </Grid>
                      <TextInput label="Address" value={draft.address ?? ''}
                        onChange={(e) => setDraft({ ...draft, address: e.currentTarget.value })} />

                      <Group justify="space-between" mt="xs">
                        <Text fw={600} size="sm">Contacts ({draft.contacts.length} of {MAX_BANK_CONTACTS})</Text>
                        {editMode && (
                          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
                            disabled={draft.contacts.length >= MAX_BANK_CONTACTS}
                            onClick={() => setDraft({ ...draft, contacts: [...draft.contacts, { ...EMPTY_CONTACT }] })}>
                            Add contact
                          </Button>
                        )}
                      </Group>
                      {draft.contacts.length === 0 ? (
                        <Text size="sm" c="dimmed">No contacts.</Text>
                      ) : (
                        <Table withTableBorder fz="sm" verticalSpacing={4}>
                          <Table.Thead>
                            <Table.Tr><Table.Th>Name</Table.Th><Table.Th>Email</Table.Th><Table.Th>Phone</Table.Th>
                              <Table.Th>Mobile</Table.Th><Table.Th>Notes</Table.Th>
                              {(['to', 'cc'] as const).map((role) => (
                                <Table.Th key={role} w={70} ta="center">
                                  <Tooltip withArrow multiline w={280} label={role === 'to'
                                    ? "Trade orders To: the bank's trade-desk addresses; the user picks among them when sending trade orders for execution. Needs an email."
                                    : 'Trade orders Cc: always copied on trade orders sent for execution. Needs an email.'}>
                                    <span>Trade orders {role === 'to' ? 'To' : 'Cc'}</span>
                                  </Tooltip>
                                </Table.Th>
                              ))}
                              {editMode && <Table.Th w={40} />}</Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {draft.contacts.map((c, i) => (
                              <Table.Tr key={i}>
                                {(['name', 'email', 'phone', 'mobile', 'notes'] as const).map((k) => (
                                  <Table.Td key={k}>
                                    <TextInput size="xs" variant={editMode ? 'default' : 'unstyled'} value={c[k] ?? ''}
                                      onChange={(e) => setContact(i, k, e.currentTarget.value)} />
                                  </Table.Td>
                                ))}
                                {/* TF-S1 (Pedro 2026-10-25): two tick columns; ticking one clears the other. */}
                                {(['to', 'cc'] as const).map((role) => {
                                  const noEmail = !(c.email ?? '').trim();
                                  return (
                                    <Table.Td key={role} ta="center">
                                      <Tooltip withArrow disabled={!noEmail || !editMode} label="Add an email first">
                                        <span style={{ display: 'inline-block' }}>
                                          <Checkbox size="xs" checked={c.trade_role === role} disabled={!editMode || (noEmail && c.trade_role !== role)}
                                            aria-label={`Trade orders ${role === 'to' ? 'To' : 'Cc'}: ${c.name ?? ''}`}
                                            onChange={(e) => setContact(i, 'trade_role', e.currentTarget.checked ? role : null)} />
                                        </span>
                                      </Tooltip>
                                    </Table.Td>
                                  );
                                })}
                                {editMode && (
                                  <Table.Td>
                                    <Tooltip withArrow label="Remove this contact (on Save)">
                                      <ActionIcon variant="subtle" color="red" aria-label="Remove contact"
                                        onClick={() => setDraft({ ...draft, contacts: draft.contacts.filter((_, j) => j !== i) })}>
                                        <IconX size={14} />
                                      </ActionIcon>
                                    </Tooltip>
                                  </Table.Td>
                                )}
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      )}
                    </Stack>
                  </fieldset>

                  {detail && detail.review_notes.length > 0 && (
                    <Alert color="yellow" title={`To review (${detail.review_notes.length})`} mt="xs">
                      <Stack gap={4}>
                        <Text size="xs">
                          Left when the Banks list was built from the accounts. Check each one, fix the bank or the account
                          if needed, then dismiss it.
                        </Text>
                        {detail.review_notes.map((n) => (
                          <Group key={n.id} justify="space-between" wrap="nowrap" gap="xs">
                            <Text size="sm">
                              <b>{REVIEW_NOTE_KINDS[n.kind] ?? n.kind}</b>
                              {n.account_label ? ` · account ${n.account_label}` : ''}: {n.detail}
                            </Text>
                            <Button size="compact-xs" variant="subtle" leftSection={<IconCheck size={12} />}
                              loading={dismiss.isPending && dismiss.variables === n.id}
                              onClick={() => dismiss.mutate(n.id)}>
                              Dismiss
                            </Button>
                          </Group>
                        ))}
                      </Stack>
                    </Alert>
                  )}
                </Stack>
              </Paper>
            )}
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
